import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import './HighlightsForm.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function AdmissionsOnlineApplicationForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    summary: '',
    note: '',
    ctaLabel: '',
    image: '',
    highlights: []
  });
  const [pendingFields, setPendingFields] = useState({
    title: false,
    subtitle: false,
    summary: false,
    note: false,
    ctaLabel: false,
    image: false
  });
  const [pendingHighlights, setPendingHighlights] = useState({});
  const [originalData, setOriginalData] = useState({ ...formData });
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [tempHighlight, setTempHighlight] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);
  const [mediaMap, setMediaMap] = useState({});

  useEffect(() => {
    loadData();
    loadMediaMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(
        `${apiBase}/admissions/cms/media`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      if (response.ok) {
        const result = await response.json();
        if (result.ok && Array.isArray(result.data)) {
          const map = {};
          result.data.forEach(media => {
            const mediaId = media.id || media.media_id;
            const mediaUrl = media.url || media.file_path;
            if (mediaId && mediaUrl) {
              map[mediaId] = mediaUrl;
            }
          });
          setMediaMap(map);
        }
      }
    } catch (err) {
      console.warn('[AdmissionsOnlineApplicationForm] Media fetch error:', err.message);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiBase}/admissions/cms/sections`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error(response.status);

      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const section = result.data.find(s => s.section_type === 'admissions-online-application');
        if (section) {
          setSectionId(section.id);
          let content = section.content_published;
          if (typeof content === 'string') content = JSON.parse(content);
          const en = content?.en || {};
          const newData = {
            title: en.title || '',
            subtitle: en.subtitle || '',
            summary: en.summary || '',
            note: en.note || '',
            ctaLabel: en.ctaLabel || '',
            image: en.image || '',
            highlights: Array.isArray(en.highlights) ? en.highlights : []
          };
          setFormData(newData);
          setOriginalData(newData);

          // Load pending changes
          const pRes = await fetch(`${apiBase}/cms/section/${section.id}/with-pending`, {
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          });
          if (pRes.ok) {
            const pResult = await pRes.json();
            if (pResult.success && pResult.data?.pending_changes) {
              const pMap = {
                title: false,
                subtitle: false,
                summary: false,
                note: false,
                ctaLabel: false,
                image: false
              };
              const hMap = {};
              pResult.data.pending_changes.forEach(c => {
                if (c.status === 'pending' && c.locale === 'en') {
                  if (Object.keys(pMap).includes(c.field_id)) {
                    pMap[c.field_id] = true;
                  } else if (c.field_id === 'highlights.update') {
                    hMap['highlights'] = true;
                  }
                }
              });
              setPendingFields(pMap);
              setPendingHighlights(hMap);
            }
          }
        }
      }
    } catch (err) {
      console.error('[AdmissionsOnlineApplicationForm] Load error:', err.message);
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (name, value) => setFormData(prev => ({ ...prev, [name]: value }));

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    console.log('[AdmissionsOnlineApplicationForm] Image upload started:', { filename: file.name });

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(`Max 5MB, got ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP allowed');
      return;
    }

    setImageUploading(true);
    setImageProgress(0);

    try {
      const formDataToUpload = new FormData();
      formDataToUpload.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          setImageProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        console.log('[AdmissionsOnlineApplicationForm] Upload completed, status:', xhr.status);
        
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.success || response.ok) {
              const mediaId = response.media_id || response.id;
              console.log('[AdmissionsOnlineApplicationForm] ✓ Image uploaded, media ID:', mediaId);
              if (mediaId) {
                setFormData(prev => ({ ...prev, image: mediaId }));
                toast.success('✓ Image uploaded (ID: ' + mediaId + ')');
              }
            }
          } catch (parseErr) {
            console.error('[AdmissionsOnlineApplicationForm] JSON parse error:', parseErr);
            toast.error('Invalid server response');
          }
        } else {
          toast.error('Upload failed (HTTP ' + xhr.status + ')');
        }
        setImageUploading(false);
      });

      xhr.addEventListener('error', () => {
        console.error('[AdmissionsOnlineApplicationForm] XHR error');
        toast.error('Upload failed - network error');
        setImageUploading(false);
      });

      xhr.open('POST', `${apiBase}/admissions/cms/media/upload?section_type=admissions-online-application`);
      xhr.withCredentials = true;
      xhr.send(formDataToUpload);
    } catch (err) {
      console.error('[AdmissionsOnlineApplicationForm] Upload error:', err);
      toast.error('Upload error: ' + err.message);
      setImageUploading(false);
    }
  };

  const handleAddHighlight = () => {
    if (!tempHighlight?.trim()) return;
    setFormData(prev => ({
      ...prev,
      highlights: [...prev.highlights, tempHighlight.trim()]
    }));
    setTempHighlight('');
  };

  const handleRemoveHighlight = (idx) => {
    setFormData(prev => ({
      ...prev,
      highlights: prev.highlights.filter((_, i) => i !== idx)
    }));
  };

  const handleSubmitField = async (name) => {
    if (!sectionId) {
      toast.error('No section');
      return;
    }
    if (!formData[name] && name !== 'image') {
      toast.warning(`Fill ${name}`);
      return;
    }
    if (formData[name] === originalData[name]) {
      toast.warning('No changes');
      return;
    }

    setSubmitting(p => ({ ...p, [name]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: name,
          field_name: name,
          old_value: originalData[name],
          new_value: formData[name],
          locale: 'en'
        })
      });
      const result = await res.json();
      if (result.success || res.ok) {
        toast.success('Submitted');
        setPendingFields(p => ({ ...p, [name]: true }));
        setOriginalData(p => ({ ...p, [name]: formData[name] }));
      } else {
        toast.error(result.error || 'Failed');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(p => ({ ...p, [name]: false }));
    }
  };

  const handleSubmitHighlights = async () => {
    if (!sectionId) {
      toast.error('No section');
      return;
    }

    setSubmitting(p => ({ ...p, highlights: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: 'highlights.update',
          field_name: 'Highlights',
          old_value: originalData.highlights,
          new_value: formData.highlights,
          locale: 'en'
        })
      });
      const result = await res.json();
      if (result.success || res.ok) {
        toast.success('Highlights submitted');
        setPendingHighlights({ highlights: true });
        setOriginalData(p => ({ ...p, highlights: formData.highlights }));
      } else {
        toast.error(result.error || 'Failed');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(p => ({ ...p, highlights: false }));
    }
  };

  if (loading) return <div className="hero-form-wrapper"><div className="loading">Loading...</div></div>;

  return (
    <div className="hero-form-wrapper">
      <ToastContainer />
      <div className="hero-form-header">
        <h1 className="hero-form-title">Admissions Online Application</h1>
        <p className="hero-form-description">Edit online application form section</p>
      </div>

      {/* Title */}
      <div className="form-section">
        <div className="field-header">
          <label>Title</label>
          {pendingFields.title && <span className="status-badge pending">Pending</span>}
        </div>
        <input
          type="text"
          value={formData.title}
          onChange={e => handleFieldChange('title', e.target.value)}
          disabled={submitting.title || pendingFields.title}
          placeholder="e.g., Online Application Form"
          className="text-input"
        />
        <button
          onClick={() => handleSubmitField('title')}
          disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
          className="submit-btn"
        >
          {submitting.title ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      {/* Subtitle */}
      <div className="form-section">
        <div className="field-header">
          <label>Subtitle</label>
          {pendingFields.subtitle && <span className="status-badge pending">Pending</span>}
        </div>
        <input
          type="text"
          value={formData.subtitle}
          onChange={e => handleFieldChange('subtitle', e.target.value)}
          disabled={submitting.subtitle || pendingFields.subtitle}
          placeholder="e.g., Online Application"
          className="text-input"
        />
        <button
          onClick={() => handleSubmitField('subtitle')}
          disabled={submitting.subtitle || pendingFields.subtitle || formData.subtitle === originalData.subtitle}
          className="submit-btn"
        >
          {submitting.subtitle ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      {/* Summary */}
      <div className="form-section">
        <div className="field-header">
          <label>Summary</label>
          {pendingFields.summary && <span className="status-badge pending">Pending</span>}
        </div>
        <textarea
          value={formData.summary}
          onChange={e => handleFieldChange('summary', e.target.value)}
          disabled={submitting.summary || pendingFields.summary}
          placeholder="Main summary text..."
          className="textarea-input"
          rows="3"
        />
        <button
          onClick={() => handleSubmitField('summary')}
          disabled={submitting.summary || pendingFields.summary || formData.summary === originalData.summary}
          className="submit-btn"
        >
          {submitting.summary ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      {/* Note */}
      <div className="form-section">
        <div className="field-header">
          <label>Note</label>
          {pendingFields.note && <span className="status-badge pending">Pending</span>}
        </div>
        <textarea
          value={formData.note}
          onChange={e => handleFieldChange('note', e.target.value)}
          disabled={submitting.note || pendingFields.note}
          placeholder="Additional note/help text..."
          className="textarea-input"
          rows="2"
        />
        <button
          onClick={() => handleSubmitField('note')}
          disabled={submitting.note || pendingFields.note || formData.note === originalData.note}
          className="submit-btn"
        >
          {submitting.note ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      {/* CTA Label */}
      <div className="form-section">
        <div className="field-header">
          <label>CTA Button Label</label>
          {pendingFields.ctaLabel && <span className="status-badge pending">Pending</span>}
        </div>
        <input
          type="text"
          value={formData.ctaLabel}
          onChange={e => handleFieldChange('ctaLabel', e.target.value)}
          disabled={submitting.ctaLabel || pendingFields.ctaLabel}
          placeholder="e.g., Apply Online Now"
          className="text-input"
        />
        <button
          onClick={() => handleSubmitField('ctaLabel')}
          disabled={submitting.ctaLabel || pendingFields.ctaLabel || formData.ctaLabel === originalData.ctaLabel}
          className="submit-btn"
        >
          {submitting.ctaLabel ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      {/* Image */}
      <div className="form-section">
        <div className="field-header">
          <label>Section Image</label>
          {pendingFields.image && <span className="status-badge pending">Pending</span>}
        </div>
        
        {/* Image Preview */}
        {formData.image && (
          <div style={{ marginBottom: '1rem' }}>
            <div style={{
              width: '100%',
              height: '200px',
              background: '#f1f5f9',
              borderRadius: '8px',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {mediaMap[formData.image] ? (
                <img 
                  src={mediaMap[formData.image]} 
                  alt="Section"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : null}
              <div style={{
                display: mediaMap[formData.image] ? 'none' : 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#cbd5e0',
                fontWeight: '600',
                fontSize: '0.875rem'
              }}>
                ID: {formData.image} (URL not found)
              </div>
            </div>
          </div>
        )}

        {/* File Upload Area */}
        <div className="modal-upload-area">
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleImageUpload}
            disabled={imageUploading || pendingFields.image}
            className="modal-file-input"
            id="admissions-online-app-image"
          />
          <label
            htmlFor="admissions-online-app-image"
            className="modal-upload-label"
          >
            <span className="upload-icon-new">📤</span>
            <span>
              {imageUploading ? `Uploading ${imageProgress}%...` : 'Click to upload image'}
            </span>
          </label>
        </div>
        <span className="hint">Max 5MB • Recommended: 800×400px</span>

        <button
          onClick={() => handleSubmitField('image')}
          disabled={submitting.image || pendingFields.image || formData.image === originalData.image}
          className="submit-btn"
          style={{ marginTop: '1rem' }}
        >
          {submitting.image ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      {/* Highlights */}
      <div className="form-section">
        <div className="items-section-header">
          <h2 className="form-section-title">Highlights</h2>
          {pendingHighlights.highlights && <span className="status-badge pending">Pending</span>}
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          <input
            type="text"
            value={tempHighlight}
            onChange={e => setTempHighlight(e.target.value)}
            onKeyPress={e => e.key === 'Enter' && handleAddHighlight()}
            placeholder="Add highlight..."
            style={{
              flex: 1,
              padding: '0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '0.95rem'
            }}
          />
          <button
            type="button"
            onClick={handleAddHighlight}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Add
          </button>
        </div>

        {formData.highlights.length > 0 ? (
          <ul
            style={{
              margin: 0,
              paddingLeft: '1.25rem',
              listStyle: 'disc',
              marginBottom: '1rem'
            }}
          >
            {formData.highlights.map((highlight, idx) => (
              <li
                key={idx}
                style={{
                  fontSize: '0.875rem',
                  marginBottom: '0.5rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span>{highlight}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveHighlight(idx)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#ef4444',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: 'bold'
                  }}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div style={{ padding: '1rem', textAlign: 'center', color: '#6b7280', fontSize: '0.875rem' }}>
            No highlights added yet
          </div>
        )}

        <button
          onClick={handleSubmitHighlights}
          disabled={submitting.highlights || JSON.stringify(formData.highlights) === JSON.stringify(originalData.highlights)}
          className="submit-btn"
          style={{
            background: JSON.stringify(formData.highlights) === JSON.stringify(originalData.highlights) ? '#d1d5db' : '#10b981'
          }}
        >
          {submitting.highlights ? 'Submitting...' : 'Submit Highlights'}
        </button>
      </div>
    </div>
  );
}
